import IconButton from '@/components/ui/custom/buttons/IconButton';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import Text from '@/components/ui/custom/dataDisplay/Text';
import MultiFileUploader from '@/components/ui/custom/input/MultiFileUploader';
import Label from '@/components/ui/custom/secondary/Label';
import SecondaryTextArea from '@/components/ui/custom/secondary/SecondaryTextArea';
import { MAX_PROMPT_FILES_COUNT, MAX_PROMPT_FILES_SIZE } from '@/constants';
import { updatePromptInstruction } from '@/data/services/promptInstructionServices';
import { promptInstructionsQueryKeys } from '@/data/services/querykeys';
import { AstroFile } from '@/models/AstroFile';
import PromptInstruction from '@/models/PromptInstruction';
import { buildChangedObject } from '@/utils/buildChangedObject';
import {
  adapterPromptFileToAstroFile,
  syncFilesWithBackend,
} from '@/utils/prompt';
import alert from '@/utils/shared/alert';
import { ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type PromptsFormProps = {
  prompt: PromptInstruction;
  onOpenHistory: () => void;
};

type FormValues = Pick<PromptInstruction, 'instruction' | 'role'> & {
  files: AstroFile[];
};

export default function PromptsForm({
  prompt,
  onOpenHistory,
}: PromptsFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts',
  });
  const queryClient = useQueryClient();

  const [hasChanges, setHasChanges] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      instruction: prompt.instruction,
      role: prompt.role || '',
      files:
        prompt.files?.map(file => adapterPromptFileToAstroFile(file)) || [],
    },
  });

  useEffect(() => {
    const subscription = watch(() => {
      setHasChanges(true);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePromptChange = async ({
    instruction,
    role,
    files,
  }: FormValues) => {
    const changes = buildChangedObject(prompt, {
      instruction,
      role,
    });

    if (!isEmpty(changes)) {
      await updatePromptInstruction(prompt.id, changes);
    }

    await syncFilesWithBackend({
      currentFiles: prompt.files || [],
      localFiles: files,
      promptId: prompt.id,
    });
  };

  const { mutate: updatePrompt, isPending } = useMutation({
    mutationFn: handlePromptChange,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: promptInstructionsQueryKeys.get(prompt.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: promptInstructionsQueryKeys.list._def,
      });
      queryClient.invalidateQueries({
        queryKey: promptInstructionsQueryKeys.history(prompt.id).queryKey,
      });
      alert.success(t('form.success'));
      setHasChanges(false);
    },
  });

  const onSubmit = (data: FormValues) => {
    updatePrompt(data);
  };

  const onCancel = () => {
    reset();
    setHasChanges(false);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4 grow"
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-primary" />
          <Text
            format="poppins-600"
            text={t(`categories.${prompt.category}.name`)}
            className="text-primary"
          />
        </div>
        <IconButton
          icon={<ClockIcon className="w-5 h-5" />}
          onClick={onOpenHistory}
          type="button"
          tooltip={t('history.title')}
          className="text-primary"
        />
      </header>

      <Text
        text={t(`categories.${prompt.category}.description`)}
        className="text-sm text-base-content"
      />

      <div className="flex flex-col gap-4 grow">
        <SecondaryTextArea
          {...register('role')}
          label={{
            text: t('form.roleLabel'),
            position: 'outside',
            info: t('form.roleInfo'),
          }}
          error={errors.role?.message}
        />
        <SecondaryTextArea
          {...register('instruction', { required: 'required' })}
          label={{
            text: t('form.instructionLabel'),
            position: 'outside',
            info: t('form.instructionInfo'),
          }}
          error={errors.instruction?.message}
          className={{
            div: 'h-full',
          }}
        />
        <div className="flex flex-col gap-2">
          <Label
            label={{
              text: t('form.filesLabel'),
              className: 'text-14',
              info: t('form.filesInfo'),
            }}
          />
          <Controller
            name="files"
            control={control}
            render={({ field }) => (
              <MultiFileUploader
                value={field.value}
                onChange={field.onChange}
                label={t('form.filesLabel')}
                accept=".pdf,.txt,.png,.jpg,.jpeg,.bmp,.webp"
                limit={MAX_PROMPT_FILES_SIZE}
                maxFiles={MAX_PROMPT_FILES_COUNT}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <SaveCancelGroup
          cancel={{
            text: t('form.cancel'),
            disabled: isPending,
            onClick: onCancel,
          }}
          save={{
            text: t('form.save'),
            disabled: isPending || !hasChanges,
            loading: isPending,
          }}
        />
      </div>
    </form>
  );
}
